import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { BaseWizardService } from '../base.wizard.service';
import {
  ApiService,
  InputDatastoreService,
  DialogService,
  CountryListService,
  ValuesExchangeProvider,
  EntityCacheProvider,
  MainEntitiesProvider,
  CommonRegEx,
  ApplicationsService,
  IApplication
} from '@nrwl/common-lib';
import { ApplicationWizardStep } from './application.wizard.step';
import { cloneDeep, get, last } from 'lodash';

@Injectable()
export class ApplicationWizardService extends BaseWizardService {
  unsubscribe: Subject<void> = new Subject();
  openNew: boolean;
  state: ActivatedRoute;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentParam: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentAppId: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appDataUrl: any;

  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    inputDatastoreService: InputDatastoreService,
    countryListService: CountryListService,
    dialogService: DialogService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly entityCache: EntityCacheProvider,
    private readonly applicationsService: ApplicationsService,
    readonly mainEntities: MainEntitiesProvider
  ) {
    super(apiService, inputDatastoreService, countryListService, dialogService);
    this.state = state;
    this.stepClass = ApplicationWizardStep;
    this.openNew = false;
  }

  //------------------------------------------------ Init config/data ----------------------------------------------------
  openWizard(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    wizardId,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    wizardConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
  ): void {
    super.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
    if (this.editMode) {
      this._setOutputForEditMode(defaultParamsById);
      this.currentStep = last(this.steps);
      const appId = get(defaultParamsById, 'id');
      this.appDataUrl = this.appDataUrl ? this.appDataUrl : `${this.dataUrl}`;
      this.dataUrl = `${this.appDataUrl}/${appId}`;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  _setDefaultParams(step, targetOutput, defaultParamsById: any = {}): void {
    super._setDefaultParams(step, targetOutput, defaultParamsById);
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  showExitInsteadOfClose(): boolean {
    if (this.isFinishingStep() && (this.requestFailed || this.errorMessage)) {
      return true;
    }
    return false;
  }

  setNextStep(): void {
    // skip active tests page, as there is no way yet to check if active tests license is installed.
    // tslint:disable-next-line:max-line-length
    if (
      this.currentStep.id === 'wizardService.new_application.app_basic' ||
      this.currentStep.id === 'wizardService.update_application.app_basic'
    ) {
      this._clearSkippedPageOutput(this.steps[this.currentStep.config.index + 1]);
      this.currentStep = this.steps[this.currentStep.config.index + 2];
    } else {
      super.setNextStep();
    }
  }

  _setPreviousStep(): void {
    //tslint:disable-next-line:max-line-length
    if (
      this.currentStep.id === 'wizardService.new_application.review' ||
      this.currentStep.id === 'wizardService.update_application.review'
    ) {
      // skip active tests page, as there is no way yet to check if active tests license is installed.
      this.currentStep = this.steps[this.currentStep.config.index - 2];
    } else {
      super._setPreviousStep();
    }
    // Initalize value from default params in edit mode
    this._initStepData(this.currentStep, this.currentStep, this.defaultParamsById);
  }

  retrySaveRequest(): void {
    return super._saveRequest(this.lastSubmittedOutput, null);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFinish(): any {
    //Save last step data
    this.currentStep.onNext();
    this.dirtyFlag = false;
    const output = this._getFormatOutput();
    this.lastSubmittedOutput = cloneDeep(output);
    const re = new RegExp(CommonRegEx.URL_START_REGEX, 'i');
    if (output.url && !re.test(output.url)) {
      output.url = 'http://' + output.url;
    }
    const responseProcessed = this._saveRequest(output, null);

    responseProcessed.subscribe(result => {
      // all done, the only thing we need -> to move over to finishing step.
      this.currentAppId = result.id;
      this.exchange.notifyListeners('new_app', [result]);
      this.mainEntities.reloadEntities();
      this.applicationsService.updateApplicationResponse(result);
      this.currentStep = this.finishingStep;
    });
    // we need to inform new.application.wizard about results of operation, so that
    // it can create the component, representing the finishingStep object.
    return responseProcessed;
  }

  getApps(): IApplication[] {
    const cachedApps = this.entityCache.findEntityCache('application');
    const filteredData = cachedApps.getValues().map(a => a.applicationData);
    return filteredData.filter(app => !app.deleted);
  }
}
